/**
 * Delivery Center Chat Pending Count — singleton service.
 *
 * Polls /sales/getDeliveryCenterUnreadCount every 10 min (only when tab is
 * visible) and notifies subscribed React components when the count changes.
 * The backend scopes the count by the logged-in user's role (agent: own
 * orders, TL/FM/manager: team orders, admin/ops: all orders) and counts only
 * messages from the opposite side (SCM vs Agent/Manager) not yet read by
 * this user.
 *
 * Usage:
 *   bulkDeliveryCenterPendingCount.subscribe(setter)  → returns unsubscribe fn
 *   bulkDeliveryCenterPendingCount.refresh()          → force immediate fetch
 *   bulkDeliveryCenterPendingCount.current            → last known count
 */

import axios from 'axios';

const BULK_PATH  = process.env.REACT_APP_PATH_BULK;
const POLL_MS     = 10 * 60 * 1000; // 10 min (Shailendra 2026-09-24, was 30s)
const TIMEOUT_MS  = 10000;

let current       = 0;
const subscribers = new Set();
let timer         = null;
let inFlight      = null;

async function fetchNow() {
  if (!BULK_PATH) return;
  if (inFlight) return inFlight;
  inFlight = axios.post(`${BULK_PATH}/sales/getDeliveryCenterUnreadCount`, {}, { timeout: TIMEOUT_MS })
    .then((res) => {
      const next = Number(res.data?.data?.count ?? 0);
      if (next !== current) {
        current = next;
        for (const set of subscribers) set(current);
      }
    })
    .catch((err) => { console.warn('[BulkDeliveryCenterPendingCount] fetch failed:', err.message); })
    .finally(() => { inFlight = null; });
  return inFlight;
}

function startPoll() {
  if (timer) return;
  timer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    fetchNow();
  }, POLL_MS);
}

function stopPoll() {
  if (!timer) return;
  clearInterval(timer);
  timer = null;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fetchNow();
  });
}

const bulkDeliveryCenterPendingCount = {
  subscribe(setter) {
    subscribers.add(setter);
    setter(current);
    if (subscribers.size === 1) {
      startPoll();
      fetchNow();
    }
    return () => {
      subscribers.delete(setter);
      if (subscribers.size === 0) stopPoll();
    };
  },
  refresh: fetchNow,
  get current() { return current; },
};

export default bulkDeliveryCenterPendingCount;
